/**
 * The team's saved signatures on the page — the logic, kept JSX-free so the node-only Jest setup can
 * test it.
 *
 * Signatures are stored by the API (`internal-admin/email-signatures`), not in the browser: the iOS
 * install step opens the generator ON THE PHONE, where a browser-local copy would not exist, and the
 * team wants several signatures per person (German and English, one per role) to reopen and edit.
 *
 * Three rules live here because each goes wrong quietly inside a component:
 *
 * - **Dirty is judged on what is STORED.** The server stores the form normalised (whitespace
 *   collapsed), so a field that differs from the saved row only by a trailing space is not an
 *   unsaved change — otherwise every reload of a pasted name would nag about unsaved edits.
 * - **A suggested name is unique.** "Erik Arens (DE)" twice in a list is two rows nobody can tell
 *   apart; the second becomes "Erik Arens (DE) 2".
 * - **The server's field errors are read through a guard**, never trusted: a 400 from an older API,
 *   or from a proxy, must fall back to a generic message rather than crash the form.
 */

import axios from 'axios';
import {
  defaultSignatureInput,
  isSignatureLocale,
  normaliseField,
  type SignatureErrors,
  type SignatureField,
  type SignatureFieldError,
  type SignatureInput,
  type SignatureLocale,
} from './signature';

/** The editor's contents: the signature plus the name it is listed under. */
export interface SignatureForm extends SignatureInput {
  readonly name: string;
}

/** A saved signature as the list shows it — the API's `EmailSignatureDto`, structurally. */
export interface SavedSignature extends SignatureForm {
  readonly id: string;
  readonly createdBy: string;
  readonly updatedBy: string;
  readonly createdAt: string;
  /** The compare-and-swap version; sent back as `expectedUpdatedAt`. */
  readonly updatedAt: string;
}

/** Every field of a form, in a fixed order — what dirty-checking and the request body walk. */
const FORM_FIELDS = [
  'name',
  'fullName',
  'jobTitle',
  'email',
  'phone',
  'mobile',
  'photoUrl',
  'locale',
  'showLogo',
  'showLegal',
] as const satisfies readonly (keyof SignatureForm)[];

/** Exactly the form's fields of anything that has them — no id, no version, no extra keys. */
function pickForm(source: SignatureForm): SignatureForm {
  return {
    name: source.name,
    fullName: source.fullName,
    jobTitle: source.jobTitle,
    email: source.email,
    phone: source.phone,
    mobile: source.mobile,
    photoUrl: source.photoUrl,
    locale: source.locale,
    showLogo: source.showLogo,
    showLegal: source.showLegal,
  };
}

/** The editable form of a saved signature. */
export function formFromSaved(saved: SavedSignature): SignatureForm {
  return pickForm(saved);
}

/**
 * A name nobody in the list has yet: `base`, else `base 2`, `base 3` … Compared normalised and
 * case-insensitively, the way a reader tells two rows apart.
 */
export function uniqueSignatureName(
  base: string,
  existingNames: readonly string[],
): string {
  const taken = new Set(
    existingNames.map((name) => normaliseField(name).toLowerCase()),
  );
  const root = normaliseField(base);
  if (!taken.has(root.toLowerCase())) return root;
  for (let suffix = 2; ; suffix += 1) {
    const candidate = `${root} ${suffix}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

/**
 * The name a new signature is suggested under — "Erik Arens (DE)". The language tag is in it
 * because the commonest pair in the list is one person's German and English signature.
 *
 * @param fallback - Used when the full name is still empty (the catalogue's "New signature").
 */
export function suggestSignatureName(
  fullName: string,
  locale: SignatureLocale,
  existingNames: readonly string[],
  fallback: string,
): string {
  const who = normaliseField(fullName) || fallback;
  return uniqueSignatureName(`${who} (${locale.toUpperCase()})`, existingNames);
}

/** A fresh, unsaved form, prefilled with the signed-in operator and a unique name. */
export function newSignatureForm(
  displayName: string | null,
  locale: SignatureLocale,
  existingNames: readonly string[],
  fallbackName: string,
): SignatureForm {
  const input = defaultSignatureInput(displayName ?? '', locale);
  return {
    ...input,
    name: suggestSignatureName(
      input.fullName,
      locale,
      existingNames,
      fallbackName,
    ),
  };
}

/**
 * The form a "duplicate" starts from: the same signature, unsaved, under a unique copy name.
 *
 * @param copySuffix - The catalogue's "(copy)", so the name follows the operator's UI language.
 */
export function duplicateSignatureForm(
  form: SignatureForm,
  existingNames: readonly string[],
  copySuffix: string,
): SignatureForm {
  return {
    ...form,
    name: uniqueSignatureName(`${form.name} ${copySuffix}`, existingNames),
  };
}

/** A field's value as the server would store it. */
function stored(form: SignatureForm, field: (typeof FORM_FIELDS)[number]) {
  const value = form[field];
  if (typeof value !== 'string') return value;
  return field === 'photoUrl' ? value.trim() : normaliseField(value);
}

/**
 * Whether two forms differ in anything that would be STORED — the test behind "unsaved changes".
 *
 * The page compares against where the form STARTED: the saved version for a signature that
 * exists, the prefilled form for a new one. So a freshly opened "new signature" nobody has typed
 * into is not an unsaved edit worth a leave-page warning, while one with a name typed into it is.
 */
export function signatureFormsDiffer(
  reference: SignatureForm,
  form: SignatureForm,
): boolean {
  return FORM_FIELDS.some(
    (field) => stored(form, field) !== stored(reference, field),
  );
}

/**
 * The request body for a save — every field, as typed; the server normalises. Picked field by
 * field, because the production ValidationPipe refuses any key the DTO does not declare.
 */
export function signatureContent(form: SignatureForm): SignatureForm {
  return pickForm(form);
}

/** The server's per-field verdict: the generator's field codes, plus the name's. */
export type SignatureServerErrors = SignatureErrors & {
  readonly name?: 'required' | 'tooLong';
};

const FIELD_ERRORS = new Set<string>([
  'required',
  'tooLong',
  'invalidEmail',
  'invalidPhone',
  'invalidUrl',
] satisfies SignatureFieldError[]);

const FIELDS = new Set<string>([
  'fullName',
  'jobTitle',
  'email',
  'phone',
  'mobile',
  'photoUrl',
] satisfies SignatureField[]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFieldError(value: unknown): value is SignatureFieldError {
  return typeof value === 'string' && FIELD_ERRORS.has(value);
}

function isField(value: string): value is SignatureField {
  return FIELDS.has(value);
}

/**
 * The per-field errors of a 400 the API answered a save with, or null when the error is anything
 * else (network, 409, a 400 without `fieldErrors`). Unknown fields and codes are dropped.
 */
export function signatureServerErrors(
  error: unknown,
): SignatureServerErrors | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 400) return null;
  // The API sends the verdict as `{ code: EMAIL_SIGNATURE_INVALID, params: { fieldErrors } }` —
  // `code`/`params` being what its global exception filter forwards.
  const body: unknown = error.response.data;
  if (!isRecord(body) || body['code'] !== 'EMAIL_SIGNATURE_INVALID')
    return null;
  const params = body['params'];
  if (!isRecord(params) || !isRecord(params['fieldErrors'])) return null;
  const errors: {
    -readonly [K in keyof SignatureServerErrors]: SignatureServerErrors[K];
  } = {};
  for (const [field, code] of Object.entries(params['fieldErrors'])) {
    if (field === 'name' && (code === 'required' || code === 'tooLong')) {
      errors.name = code;
    } else if (isField(field) && isFieldError(code)) {
      errors[field] = code;
    }
  }
  return Object.keys(errors).length > 0 ? errors : null;
}

/** Whether a save was refused because the signature changed under the operator (409). */
export function isSignatureConflict(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 409;
}

/** Whether the signature no longer exists (404) — another operator deleted it. */
export function isSignatureGone(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404;
}

/** Narrows a stored locale, reading anything unexpected as German (as the API does). */
export function savedLocale(value: string): SignatureLocale {
  return isSignatureLocale(value) ? value : 'de';
}
